import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTULO_TIPO_CONTATO, type TipoContato } from '@reducee/contracts';
import { useState } from 'react';
import { Link } from 'react-router';

import {
  BotaoDePagina,
  CabecalhoDePagina,
  CabecalhoDeTabela,
  CLASSE_TABELA,
} from '../componentes/Superficie.tsx';
import { chamarApi } from '../api/cliente.ts';

type Contato = {
  id: string;
  tipo: TipoContato;
  nome: string;
  documento: string | null;
  telefone: string | null;
  email: string | null;
  naoContatar: boolean;
  ehCliente: boolean;
  totalLeads: number;
  totalOportunidades: number;
};

type PaginaDeContatos = {
  contatos: Contato[];
  total: number;
  pagina: number;
  porPagina: number;
};

/**
 * Identidades conhecidas pela Reducee.
 *
 * Um Contato aparece uma única vez, por mais jornadas comerciais que tenha. É
 * Cliente somente quando já possui Contrato.
 *
 * Paginada e com busca (NEX-231): cada importação traz dezenas de Contatos de
 * uma vez, e a tabela única passou de uma tela para várias.
 */
export function PaginaContatos(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);

  const consulta = useQuery({
    // Debaixo de `contatos`, para que bloquear e importar, que invalidam essa
    // chave, também recarreguem a página aberta.
    queryKey: ['contatos', 'pagina', busca.trim(), pagina],
    queryFn: () =>
      chamarApi<PaginaDeContatos>(
        `/contatos/pagina?pagina=${pagina}&busca=${encodeURIComponent(busca.trim())}`,
      ),
    // Sem isto a troca de página pisca a tabela vazia entre uma busca e outra.
    placeholderData: (anterior) => anterior,
  });
  const dados = consulta.data;
  const paginas = dados ? Math.max(1, Math.ceil(dados.total / dados.porPagina)) : 1;

  const alternarBloqueio = useMutation({
    mutationFn: ({ id, naoContatar }: { id: string; naoContatar: boolean }) =>
      chamarApi(`/contatos/${id}/bloqueio`, { metodo: 'PATCH', corpo: { naoContatar } }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['contatos'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['atender-agora'] });
    },
  });

  return (
    <section>
      <CabecalhoDePagina
        area="Comercial"
        titulo="Contatos"
        descricao="A identidade de cada pessoa ou empresa, separada das suas entradas comerciais."
      />

      <input
        type="search"
        value={busca}
        onChange={(evento) => {
          setBusca(evento.target.value);
          // A página 4 da busca anterior quase nunca existe na nova, e cair
          // numa tabela vazia parece defeito.
          setPagina(1);
        }}
        placeholder="Buscar por nome, documento, telefone ou e-mail…"
        aria-label="Buscar contatos"
        className="w-full max-w-md rounded-lg border border-linha px-3 py-2 outline-none focus:border-marinho-claro"
      />

      <div className="mt-4 overflow-x-auto rounded-xl bg-superficie ring-1 ring-linha">
        <table className={CLASSE_TABELA}>
          <CabecalhoDeTabela colunas={['Nome', 'Tipo', 'Contato', 'Leads', 'Oportunidades', 'Situação', 'Ações']} />
          <tbody>
            {dados?.contatos.map((contato) => (
              <tr key={contato.id} className="border-b border-linha last:border-0">
                <td className="px-3.25 py-3 font-medium">
                  {/* O nome é o alvo que a mão procura primeiro; o "Abrir" da
                      última coluna fica para quem lê a linha até o fim. */}
                  <Link
                    to={`/contatos/${contato.id}`}
                    data-teste="nome-do-contato"
                    className="text-marinho underline-offset-2 hover:underline"
                  >
                    {contato.nome}
                  </Link>
                </td>
                <td className="px-3.25 py-3">{ROTULO_TIPO_CONTATO[contato.tipo]}</td>
                <td className="px-3.25 py-3">
                  {contato.telefone ?? contato.email ?? contato.documento ?? '—'}
                </td>
                <td className="px-3.25 py-3">{contato.totalLeads}</td>
                <td className="px-3.25 py-3">{contato.totalOportunidades}</td>
                <td className="px-3.25 py-3">
                  <span className="flex flex-wrap gap-1">
                    {contato.ehCliente ? (
                      <span className="rounded-full bg-sucesso/10 px-2 py-0.5 text-xs font-medium text-sucesso">
                        Cliente
                      </span>
                    ) : null}
                    {contato.naoContatar ? (
                      <span
                        data-teste="marca-nao-contatar"
                        className="rounded-full bg-erro/10 px-2 py-0.5 text-xs font-medium text-erro"
                      >
                        Não contatar
                      </span>
                    ) : null}
                    {!contato.ehCliente && !contato.naoContatar ? '—' : null}
                  </span>
                </td>
                <td className="px-3.25 py-3">
                  <div className="flex gap-2">
                    <Link
                      to={`/contatos/${contato.id}`}
                      className="rounded-lg border border-linha px-3 py-1 transition hover:bg-marinho-suave"
                    >
                      Abrir
                    </Link>
                    <button
                      type="button"
                      onClick={() =>
                        alternarBloqueio.mutate({
                          id: contato.id,
                          naoContatar: !contato.naoContatar,
                        })
                      }
                      className="rounded-lg border border-linha px-3 py-1 transition hover:bg-marinho-suave"
                    >
                      {contato.naoContatar ? 'Liberar contato' : 'Não contatar'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {consulta.isLoading ? <p className="p-4 text-texto-suave">Carregando…</p> : null}
        {dados?.contatos.length === 0 ? (
          <p className="p-4 text-texto-suave">
            {busca.trim() ? 'Nenhum contato nesta busca.' : 'Nenhum contato registrado ainda.'}
          </p>
        ) : null}
      </div>

      {/* Como na trilha de Auditoria: o rodapé fica mesmo com uma página só,
          porque o total é a primeira resposta da lista, e os botões ficam
          desabilitados no lugar, para não mudarem de posição ao virar. */}
      {dados && dados.total > 0 ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-texto-suave" data-teste="posicao-dos-contatos">
            Página {dados.pagina} de {paginas} · {dados.total}{' '}
            {dados.total === 1 ? 'contato' : 'contatos'}
          </p>
          <div className="flex gap-2">
            <BotaoDePagina
              rotulo="Anterior"
              desabilitado={dados.pagina <= 1}
              aoClicar={() => setPagina((atual) => Math.max(1, atual - 1))}
            />
            <BotaoDePagina
              rotulo="Próxima"
              desabilitado={dados.pagina >= paginas}
              aoClicar={() => setPagina((atual) => Math.min(paginas, atual + 1))}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
